import { useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router";
import { Menu } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import SitesIcon from "@/assets/icons/sites.svg?react";

import { useScopeParams } from "@/hooks/useScopeParams";
import useIsFeatureFlagEnabled from "@/hooks/useIsFeatureFlagEnabled";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import { apiFetchJson } from "@/utils/apiFetch";

import type { TrialInfo } from "@/views/Portfolio/types";
import type { MasteredSite } from "./types";
import { createCrossTrialSiteMenuItems } from "./utils/tabItems";
import {
  PERFORMANCE_TAB_KEY,
  withPerformanceTab,
} from "./components/PerformanceTabItem";
import MasteryTab from "./tabs/MasteryTab";
import PerformanceTab from "./tabs/PerformanceTab";
import NotesTab from "@/views/shared/NotesTab";
import SurveysTab from "./tabs/SurveysTab";
import MilestonesTab from "./tabs/MilestonesTab";
import SummaryTab from "./tabs/SummaryTab";
import InvestigatorTrialsTab from "@/views/InvestigatorProfile/tabs/TrialsTab";
import RiskTab from "./tabs/RiskTab";
import SiteDocumentsTab from "./tabs/SiteDocumentsTab";

const DEFAULT_TAB = "performance";

function BlankTab({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-48 text-[#8C8C8C]">
      <span className="text-[13px]">{message}</span>
    </div>
  );
}

type PortfolioTrialApiRow = {
  trial_nct_id: string;
  trial_name: string | null;
  brief_title: string | null;
  phase: string | null;
};

// One site, resolved server-side. This page needs a single row, and the
// catalog is thousands of rows wide on a tenant like takeda — fetching all of
// them to `.find()` one was most of this screen's payload. ?site_key= also
// resolves the per-trial id shapes the redirect below used to scan for, so a
// deep link still lands on the canonical profile.
async function fetchPortfolioSite(siteKey: string): Promise<MasteredSite[]> {
  return apiFetchJson<MasteredSite[]>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/portfolio-sites/?site_key=${encodeURIComponent(siteKey)}`,
  );
}

type PaginatedTrials = { results: PortfolioTrialApiRow[]; count: number };

async function fetchPortfolioTrials(): Promise<PortfolioTrialApiRow[]> {
  const data = await apiFetchJson<PaginatedTrials | PortfolioTrialApiRow[]>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/portfolio-trials/?page_size=200`,
  );
  return Array.isArray(data) ? data : data.results;
}

export default function CrossTrialSiteProfile() {
  const { siteKey, tab } = useParams<{ siteKey: string; tab?: string }>();
  const { scope, id, buildUrl } = useScopeParams();
  const navigate = useNavigate();
  const isFlagEnabled = useIsFeatureFlagEnabled();

  // Performance is the DQS metric rollup; under SITE_TAB_GATING it follows
  // its backing-data flag (the portfolio sites-metrics screen shares the same
  // feed) so tenants without the rollup see no empty tab.
  const showPerformanceTab =
    !isFlagEnabled(FEATURE_FLAGS.SITE_TAB_GATING) ||
    isFlagEnabled(FEATURE_FLAGS.PORT_SITES_METRICS);

  // SITE_TAB_DISABLED (clario) keeps Performance listed but greyed out: the
  // tenant's feed carries no metric rollup, so the tab would only ever open on
  // its empty state.
  const performanceTabDisabled = isFlagEnabled(FEATURE_FLAGS.SITE_TAB_DISABLED);

  const simplified = isFlagEnabled(FEATURE_FLAGS.PROFILE_SIMPLIFIED);
  const notesHidden = isFlagEnabled(FEATURE_FLAGS.NOTES_HIDDEN);

  // Performance leads the simplified menu, so it is also where the profile
  // opens; everywhere else the trial list is still the front page.
  const defaultTab =
    simplified && showPerformanceTab && !performanceTabDisabled
      ? PERFORMANCE_TAB_KEY
      : DEFAULT_TAB;

  const activeTab = tab ?? defaultTab;

  const { data: matchedSites = [], isLoading: sitesLoading } = useQuery({
    queryKey: ["portfolio-site", scope, id, siteKey],
    queryFn: () => fetchPortfolioSite(siteKey ?? ""),
    enabled: !!scope && !!id && !!siteKey,
    staleTime: 5 * 60 * 1000,
  });

  const { data: apiTrials = [] } = useQuery({
    queryKey: ["portfolio-trials", scope, id],
    queryFn: () => fetchPortfolioTrials(),
    enabled: !!scope && !!id,
    staleTime: 5 * 60 * 1000,
  });

  const site = useMemo(
    () => matchedSites.find((s) => s.site_key === siteKey) ?? null,
    [matchedSites, siteKey],
  );

  // Id-space bridging: deep links sometimes carry a per-trial site id (raw
  // trial_site_id, a stripped golden id, or the 'ts:' fallback form) rather
  // than the profile's site_key. The server matches those shapes too, so a hit
  // whose site_key differs from the URL is the canonical row to redirect to.
  const redirectKey = useMemo(() => {
    if (site || !siteKey || sitesLoading) return null;
    return matchedSites[0]?.site_key ?? null;
  }, [site, siteKey, sitesLoading, matchedSites]);

  useEffect(() => {
    if (redirectKey) {
      navigate(buildUrl(`site-profiles/${redirectKey}/${activeTab}`), {
        replace: true,
      });
    }
    // Redirect once, when the key resolves. Re-running on activeTab or on
    // navigate's identity would fight the user's own tab navigation.
    // Both exhaustive-deps rules are active in this config, so both are named.
    // eslint-disable-next-line react-hooks/exhaustive-deps, @eslint-react/exhaustive-deps
  }, [redirectKey]);

  const trialIds = useMemo(
    () =>
      site ? [...new Set((site.trials ?? []).map((t) => t.trial_id))] : [],
    [site],
  );

  const menuItems = useMemo(() => {
    const items =
      createCrossTrialSiteMenuItems(trialIds.length, isFlagEnabled) ?? [];
    return showPerformanceTab
      ? withPerformanceTab(items, performanceTabDisabled, simplified)
      : items;
  }, [
    trialIds.length,
    isFlagEnabled,
    showPerformanceTab,
    performanceTabDisabled,
    simplified,
  ]);

  // Keys reachable from the menu, so hidden screens stay unreachable by URL.
  const visibleTabKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const item of menuItems ?? []) {
      if (item && "children" in item) {
        for (const child of item.children ?? []) {
          if (child && "key" in child) keys.add(String(child.key));
        }
      } else if (item && "key" in item) {
        keys.add(String(item.key));
      }
    }
    return keys;
  }, [menuItems]);

  const knownTrials = useMemo(
    (): TrialInfo[] =>
      apiTrials
        .filter((t) => trialIds.includes(t.trial_nct_id))
        .map((t) => ({
          id: t.trial_nct_id,
          name: t.brief_title ?? t.trial_name ?? t.trial_nct_id,
          phase: t.phase ?? undefined,
        })),
    [apiTrials, trialIds],
  );

  if (sitesLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400 text-[13px]">
        Loading…
      </div>
    );
  }

  if (!site) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400 text-[13px]">
        Site not found.
      </div>
    );
  }

  const handleMenuSelect = (key: string) => {
    navigate(buildUrl(`site-profiles/${siteKey}/${key}`));
  };

  // A deep link to a gated-off tab resolves to the default tab.
  const contentTab = visibleTabKeys.has(activeTab) ? activeTab : defaultTab;

  const renderContent = () => {
    switch (contentTab) {
      case "performance":
        return simplified ? (
          <InvestigatorTrialsTab
            trialEntries={site.trials ?? []}
            buildUrl={buildUrl}
          />
        ) : (
          <SummaryTab
            trialEntries={site.trials ?? []}
            trials={knownTrials}
            activeTab={contentTab}
          />
        );
      case PERFORMANCE_TAB_KEY:
        return <PerformanceTab site={site} trials={knownTrials} />;
      case "mastery":
        return <MasteryTab site={site} />;
      case "risk":
        return <RiskTab site={site} />;
      case "site-documents":
        return <SiteDocumentsTab site={site} />;
      case "milestones":
        return <MilestonesTab site={site} trials={knownTrials} />;
      case "surveys":
        return <SurveysTab site={site} trials={knownTrials} />;
      case "forms":
        return <BlankTab message="No form data available." />;
      case "monitoring-visits":
        return <BlankTab message="No monitoring visit data available." />;
      case "notes":
        return notesHidden ? null : (
          <NotesTab
            entityKey={`site:${site.site_key}`}
            trialOptions={knownTrials.map((t) => ({
              value: t.id,
              label: t.id,
            }))}
          />
        );
      default:
        return (
          <SummaryTab
            trialEntries={site.trials ?? []}
            trials={knownTrials}
            activeTab={contentTab}
          />
        );
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Back link — spans full width above both columns */}
      <div className="shrink-0 px-3 pt-2">
        <Link
          to={buildUrl("site-profiles")}
          className="flex items-center gap-1.5 text-[12px] text-[#556C75] hover:text-[#2B4752] py-1 w-fit"
        >
          <LeftOutlined className="text-[10px]" />
          All Sites
        </Link>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <div className="w-[260px] min-w-[260px] sticky top-0 self-start h-screen flex flex-col gap-2 p-3 pb-6 overflow-y-auto">
          {/* Site info card */}
          <div
            className="px-3 py-3 flex flex-col gap-1"
            style={{
              borderRadius: 8,
              border: "1px solid rgba(0, 0, 0, 0.04)",
              background: "#FFF",
              boxShadow: "0 2px 15px 4px rgba(0, 0, 0, 0.02)",
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <div className="flex items-center p-[5px] rounded-[4px] bg-[#2B4752] text-white">
                <SitesIcon width={20} height={20} />
              </div>
              <span className="text-[10px] font-semibold text-[#556C75] tracking-wide uppercase">
                Site
              </span>
            </div>
            <span className="text-[14px] font-semibold text-[#1F2933] leading-tight">
              {site.site_name}
            </span>
            <span className="text-[12px] text-[#8C8C8C]">{site.city}</span>
            <span className="text-[12px] text-[#8C8C8C]">{site.country}</span>

            {(site.therapeutic_areas ?? []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {site.therapeutic_areas.map((ta) => (
                  <span
                    key={ta}
                    className="inline-flex items-center h-5 rounded-[3px] bg-[#EEF4FF] px-1.5 text-[12px] font-medium text-[#3B5FA0] whitespace-nowrap"
                  >
                    {ta}
                  </span>
                ))}
              </div>
            )}

            {site.survey_status === "complete" && (
              <div className="mt-1.5">
                <span className="inline-flex items-center h-5 rounded-[4px] bg-[#ECFDF5] px-1.5 text-[12px] font-semibold text-[#065F46]">
                  Profile on file
                </span>
              </div>
            )}
          </div>

          {/* Navigation menu */}
          <div
            className="overflow-y-auto overflow-clip"
            style={{
              borderRadius: 8,
              border: "1px solid rgba(0, 0, 0, 0.04)",
              background: "#FFF",
              boxShadow: "0 2px 15px 4px rgba(0, 0, 0, 0.02)",
            }}
          >
            <Menu
              mode="inline"
              selectedKeys={[contentTab]}
              items={menuItems}
              onClick={({ key }) => handleMenuSelect(key)}
              style={{ borderRight: "none", background: "transparent" }}
            />
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex-1 p-3">{renderContent()}</div>
        </div>
      </div>
    </div>
  );
}
